<template>
  <dn-link
    :href="teaser.canonicalSlug"
    :class="['dn-most-read-card', theme?.toLowerCase()]"
  >
    <dn-card
      v-track-event="trackBinding"
      :title="teaser.title"
      :meta="teaser.meta"
      :data-attr="data_attr"
    />
  </dn-link>
</template>

<script setup lang="ts">
const props = defineProps({
  id: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: false,
    default: null
  },
  meta: {
    type: String,
    required: false,
    default: null
  },
  theme: {
    type: String,
    required: false,
    default: null
  },
  prefetch: {
    type: Boolean,
    required: false,
    default: true
  },
  data_attr: {
    type: Number,
    required: false,
    default: null
  },
  /** See DnTeaserCard.vue for the position/total rationale. */
  position: {
    type: Number,
    required: false,
    default: 0
  },
  total: {
    type: Number,
    required: false,
    default: 0
  }
});

const trackBinding = () => ({
  eventName: 'article clicked',
  component: { zone: 'most-read', element: 'dn-card' },
  attributes: {
    ...(props.position && props.total ? { element_position: `${props.position}/${props.total}` } : {}),
    title: teaser.value.title,
    canonical_slug: teaser.value.canonicalSlug,
    meta: teaser.value.meta
  },
  // contract-only — sourced from data/props, not added to the teaser view-model
  content: {
    id: props.id || data.value?.id || '',
    type: data.value?.type || '',
    paid: data.value?.accessLevel === 'paywall',
    publication: data.value?.publication?.name || '',
    category: data.value?.categories?.find(category => category.default)?.title || data.value?.categories?.[0]?.title || '',
    topic: data.value?.topic?.name || ''
  }
});

const provider = inject('teaser_provider');

let data = ref({});
let loading = ref(false);
const teaser = computed(() => {
  const title = props.title || data.value?.title || ' ';
  return {
    title,
    canonicalSlug: data.value?.canonicalSlug || `/${props.id}`,
    data_attr: props.data_attr,
    meta: props.meta || data.value?.categories?.find(category => category.default)?.title
  };
});

const { query } = useTansQuery();

if (props.prefetch && provider) {
  try {
    const providerConfig = useRuntimeConfig()?.public?.[provider];
    const api = import.meta.client && providerConfig?.api ? providerConfig?.api : useRuntimeConfig()?.[provider]?.originApi;
    if (api) {
      const { replaceUrl } = useUrlReplace();
      const authHeader = kongApiAuthHeader(useRuntimeConfig().dnmgAuthToken as string);
      const response = query({
        api: ref(replaceUrl(api, { id: props.id })),
        options: { provider, id: props.id },
        transform: transformTeaser,
        headers: authHeader
      });
      data = response.data;
      loading = response.loading;
    }
  } catch (e) {
    throw createError(`Error fetching teaser ${props.id} data for ${provider || 'undefined provider'}`);
  }
}
</script>

<style lang="scss">
.dn-most-read-card {
  --dn-link_display: grid;
  --dn-link_color: var(--dn-most-read--link-color);
  --dn-card_border-bottom-width: var(--dn-most-read--card_border-bottom-width);
  --dn-card_border-bottom-color: var(--dn-most-read--card_border-bottom-color);
  --dn-card_border-right-width: var(--dn-most-read--card_border-right-width);
  --dn-card_border-right-color: var(--dn-most-read--card_border-right-color);
  --dn-card_padding: var(--dn-most-read--card_padding);
  --dn-card_font-family: var(--dn-most-read--card_font-family);
  --dn-card_grid-template: var(--dn-most-read--card_grid-template);
  --dn-card_title_grid-area: var(--dn-most-read--card_title_grid-area);
  --dn-card_meta_grid-area: var(--dn-most-read--card_meta_grid-area);
  --dn-card--title_spacing: var(--dn-most-read--title_spacing);
  --dn-card--meta_font-family: var(--dn-most-read--meta_font-family);
  --dn-card--meta_color: var(--dn-most-read--meta_color);
  --dn-card--meta_spacing: var(--dn-most-read--meta_spacing);

  container-type: inline-size;
}
</style>
